import { useEffect, useState } from 'react'
import {
  archiveLesson,
  errorMessage,
  getLessonDetail,
  publishLesson,
  retryDraft,
  uploadAudio,
  uploadVideo,
} from '../../services/admin'
import { LEVEL_FA } from './constants'
import { archiveQuestion, confirmed, publishQuestion } from './confirm'
import { droppedLinksMessage } from './dictionary'
import { lessonAltName, lessonName } from './lessonTitle'
import { StatusBadge } from './components/primitives'
import { SchedulePicker } from './components/SchedulePicker'
import { EditLessonForm } from './components/EditLessonForm'
import { DraftReviewForm } from './components/DraftReviewForm'
import { CreateForm } from './components/CreateForm'
import { AutoDraftForm } from './components/AutoDraftForm'

// ─── Audio cell ───────────────────────────────────────────────────────────────

/**
 * A sentence's reference audio. A one-sentence lesson — every lesson made
 * since 2026-10-13 — has a video *or* an audio: with a video the audio is
 * its sound track, taken on the server, so the cell only reports it; without
 * one the audio is uploaded here (`canUpload`). Older lessons with several
 * sentences upload each sentence's audio here too.
 */
function AudioCell({ lesson, sentence, onUpdate, canUpload }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function pick(e) {
    const file = e.target.files?.[0]; if (!file) return
    setBusy(true); setErr('')
    try { onUpdate(await uploadAudio(lesson.id, sentence.index, file)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false); e.target.value = '' }
  }

  return (
    <span className="flex items-center gap-1.5">
      {sentence.referenceAudioUrl
        ? <span className="font-medium text-green-600">✓ {sentence.referenceAudioSeconds}s</span>
        : <span className="text-amber-500">{canUpload ? '—' : 'ویدیو را دوباره آپلود کنید'}</span>}
      {canUpload && (
        <label className={`cursor-pointer rounded px-1.5 py-0.5 text-xs ${busy ? 'opacity-40' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}>
          {busy ? '…' : sentence.referenceAudioUrl ? 'تغییر' : 'آپلود'}
          <input type="file" accept="audio/*" className="hidden" onChange={pick} disabled={busy} />
        </label>
      )}
      {err && <span className="text-xs text-red-600">{err}</span>}
    </span>
  )
}

// ─── Video cell (one per lesson, not per sentence) ───────────────────────────

function VideoCell({ lesson, onUpdate }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const single = lesson.sentences.length === 1

  async function pick(e) {
    const file = e.target.files?.[0]; if (!file) return
    setBusy(true); setErr('')
    try { onUpdate(await uploadVideo(lesson.id, file)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false); e.target.value = '' }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 text-sm">
      <span className="text-slate-500">ویدیوی درس:</span>
      {lesson.videoUrl
        ? <span className="font-medium text-green-600">✓ {lesson.videoDurationSeconds}s</span>
        : <span className="text-amber-500">بدون ویدیو</span>}
      <label className={`cursor-pointer rounded px-1.5 py-0.5 text-xs ${busy ? 'opacity-40' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}>
        {busy ? '…' : lesson.videoUrl ? 'تغییر ویدیو' : 'آپلود ویدیو'}
        <input type="file" accept="video/*" className="hidden" onChange={pick} disabled={busy} />
      </label>
      {single && (
        <span className="text-xs text-slate-400">
          {lesson.videoUrl ? 'صدای تمرین از همین ویدیو گرفته می‌شود.' : 'ویدیو یا صدا؛ یکی کافی است.'}
        </span>
      )}
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  )
}

// ─── Lesson card (list view) ──────────────────────────────────────────────────

export function LessonCard({ lesson: initial, onUpdate }) {
  const [lesson, setLesson] = useState(initial)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // V2-p: the dictionary links a save invalidated (`droppedLinks`), shown until dismissed.
  const [dropped, setDropped] = useState('')

  useEffect(() => setLesson(initial), [initial])

  function merge(updated) {
    setLesson(updated)
    onUpdate(updated)
    setDropped(droppedLinksMessage(updated.droppedLinks))
  }

  async function doAction(fn, question) {
    if (!confirmed(question)) return
    setBusy(true); setErr('')
    try { merge(await fn(lesson.id)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  const allAudio = lesson.sentences.every(s => s.referenceAudioUrl)
  const doneCount = lesson.sentences.filter(s => s.referenceAudioUrl).length
  const single = lesson.sentences.length === 1
  const showTranslation = lesson.sentences.some(s => s.translationFa?.trim())

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="flex flex-1 items-start gap-2 text-start">
          <span className="mt-0.5 text-xs text-slate-400">{open ? '▾' : '▸'}</span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-800" dir="auto">{lessonName(lesson)}</span>
              {lessonAltName(lesson) && <span className="text-xs text-slate-400">/ {lessonAltName(lesson)}</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
              <span>{LEVEL_FA[lesson.level]}</span>
              <span>·</span><span>{single ? (allAudio ? 'صدا ✓' : 'بدون صدا') : `${doneCount}/${lesson.sentences.length} صدا`}</span>
              <span>·</span><span>{lesson.videoUrl ? '🎬 ویدیو دارد' : 'بدون ویدیو'}</span>
              <span>·</span><span>ترتیب {lesson.displayOrder}</span>
            </div>
          </div>
          <StatusBadge status={lesson.status} />
        </button>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!editing && (
            <button onClick={() => { setEditing(true); setOpen(true) }}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
              ویرایش
            </button>
          )}
          {lesson.status === 'draft' && (
            <button onClick={() => doAction(publishLesson, publishQuestion(lesson))} disabled={busy || !allAudio}
              title={allAudio ? 'انتشار' : single ? 'ابتدا ویدیو یا صدا را آپلود کنید' : 'ابتدا همه صداها را آپلود کنید'}
              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? '…' : 'انتشار'}
            </button>
          )}
          {lesson.status === 'published' && (
            <button onClick={() => doAction(archiveLesson, archiveQuestion(lesson))} disabled={busy}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              {busy ? '…' : 'بایگانی'}
            </button>
          )}
        </div>
      </div>

      {/* Schedule picker row */}
      {lesson.status === 'draft' && !editing && (
        <div className="border-t border-slate-50 px-4 py-1.5">
          <SchedulePicker lesson={lesson} onUpdate={merge} />
        </div>
      )}

      {err && <div className="border-t border-slate-100 bg-red-50 px-4 py-2 text-xs text-red-600">{err}</div>}
      {dropped && (
        <div className="flex items-start gap-2 border-t border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-dropped-links>
          <span className="flex-1">{dropped}</span>
          <button type="button" onClick={() => setDropped('')} aria-label="بستن هشدار" className="text-amber-500 hover:text-amber-800">✕</button>
        </div>
      )}

      {open && editing && (
        <EditLessonForm lesson={lesson} onSaved={u => { merge(u); setEditing(false) }} onCancel={() => setEditing(false)} />
      )}

      {open && !editing && (
        <div className="border-t border-slate-100">
          <VideoCell lesson={lesson} onUpdate={merge} />
        </div>
      )}

      {open && !editing && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500">
                {!single && <th className="w-8 px-4 py-2 text-start">#</th>}
                <th className="px-4 py-2 text-start">جمله</th>
                {showTranslation && <th className="px-4 py-2 text-start">فارسی</th>}
                <th className="w-36 px-4 py-2 text-start">صدا</th>
              </tr>
            </thead>
            <tbody>
              {lesson.sentences.map(s => (
                <tr key={s.index} className="border-t border-slate-50">
                  {!single && <td className="px-4 py-2 text-xs text-slate-400">{s.index + 1}</td>}
                  <td className="px-4 py-2 text-slate-700" dir="ltr">{s.textEn}</td>
                  {showTranslation && <td className="px-4 py-2 text-slate-700" dir="rtl">{s.translationFa}</td>}
                  <td className="px-4 py-2">
                    <AudioCell lesson={lesson} sentence={s} onUpdate={merge} canUpload={!single || !lesson.videoUrl} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FailedDraftCard({ lesson, onUpdate }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function retry() {
    setBusy(true); setErr('')
    try { onUpdate(await retryDraft(lesson.id)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 shadow-sm">
      <div className="min-w-0">
        <span className="font-semibold text-slate-800" dir="auto">{lessonName(lesson)}</span>
        {lessonAltName(lesson) && <span className="ms-1.5 text-xs text-slate-400">/ {lessonAltName(lesson)}</span>}
      </div>
      <p className="mt-1 text-xs text-red-700">پردازش ناموفق: {lesson.processingError ?? 'نامشخص'}</p>
      <button onClick={retry} disabled={busy}
        className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
        {busy ? '…' : 'تلاش دوباره'}
      </button>
      {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
    </div>
  )
}

/**
 * Stands in for LessonCard while a lesson is an in-flight auto-draft: polls
 * getLessonDetail every few seconds until processingStatus leaves
 * "processing", then hands off to DraftReviewForm (needsReview) or
 * FailedDraftCard (failed). Once the admin confirms, onUpdate's lesson has
 * processingStatus null and AdminPanel's normal LessonCard takes over.
 */
export function ProcessingLessonCard({ lesson: initial, onUpdate }) {
  const [lesson, setLesson] = useState(initial)

  useEffect(() => setLesson(initial), [initial])

  useEffect(() => {
    if (lesson.processingStatus !== 'processing') return undefined
    const id = setInterval(async () => {
      try {
        const fresh = await getLessonDetail(lesson.id)
        setLesson(fresh)
        onUpdate(fresh)
      } catch {
        // A transient network hiccup while polling; the next tick retries.
      }
    }, 3000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm only when the lesson or its status actually changes
  }, [lesson.id, lesson.processingStatus])

  if (lesson.processingStatus === 'processing') {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <span className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
        <div className="min-w-0">
          <span className="font-semibold text-slate-800" dir="auto">{lessonName(lesson)}</span>
          {lessonAltName(lesson) && <span className="ms-1.5 text-xs text-slate-400">/ {lessonAltName(lesson)}</span>}
          <p className="text-xs text-amber-600">در حال تشخیص گفتار ویدیو/صدا…</p>
        </div>
      </div>
    )
  }
  if (lesson.processingStatus === 'failed') {
    return <FailedDraftCard lesson={lesson} onUpdate={u => { setLesson(u); onUpdate(u) }} />
  }
  return <DraftReviewForm lesson={lesson} onConfirmed={u => { setLesson(u); onUpdate(u) }} />
}

/**
 * The lessons tab (X1): the create / auto-draft form, or the sorted list.
 * `view` lives in the shell because the header's buttons switch it; the
 * blocks below are the ones AdminPanel used to render inline, unchanged.
 */
export function LessonsTab({ view, sorted, loading, onCreated, onCancel, onUpdate }) {
  return (
    <>
      {view === 'create' && (
        <CreateForm onCreated={onCreated} onCancel={onCancel} />
      )}
      {view === 'auto-draft' && (
        <AutoDraftForm onCreated={onCreated} onCancel={onCancel} />
      )}
      {view === 'list' && sorted.length === 0 && !loading && (
        <p className="py-16 text-center text-sm text-slate-400">هنوز درسی ثبت نشده.</p>
      )}
      {view === 'list' && sorted.map(l => (
        l.processingStatus
          ? <ProcessingLessonCard key={l.id} lesson={l} onUpdate={onUpdate} />
          : <LessonCard key={l.id} lesson={l} onUpdate={onUpdate} />
      ))}
    </>
  )
}
